import { useState, useEffect } from 'react';
import { DollarSign, Activity, Clock, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#6b7280'];

function formatUsd(n) {
  if (n == null) return '$0.00';
  return n < 0.01 && n > 0 ? '<$0.01' : `$${n.toFixed(2)}`;
}

export default function CostDashboard() {
  const [today, setToday] = useState(null);
  const [month, setMonth] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const [tRes, mRes, bRes, rRes] = await Promise.all([
          fetch('/api/costs/today'),
          fetch('/api/costs/month'),
          fetch('/api/costs/breakdown?days=7'),
          fetch('/api/costs/recent?limit=20'),
        ]);
        if (tRes.ok) setToday(await tRes.json());
        if (mRes.ok) setMonth(await mRes.json());
        if (bRes.ok) setBreakdown(await bRes.json());
        if (rRes.ok) setRecent(await rRes.json());
      } catch { /* silent */ }
    }
    load();
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, []);

  const dailyData = breakdown?.daily?.map(d => ({
    date: d.date?.slice(5),
    cost: d.cost,
    calls: d.calls,
  })) || [];

  const serviceData = breakdown?.byService?.map((s, i) => ({
    name: s.service,
    value: s.cost > 0 ? s.cost : s.calls * 0.001, // Show non-zero wedge for free services
    calls: s.calls,
    cost: s.cost,
    fill: COLORS[i % COLORS.length],
  })) || [];

  return (
    <ModuleContainer title="API Cost Tracker" subtitle="Monitor spending across all external services">
      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-3 mb-6">
        {[
          { icon: DollarSign, label: "Today's Cost", value: formatUsd(today?.total), sub: `${today?.calls || 0} calls`, color: 'text-korinth-accent' },
          { icon: Activity, label: 'Month-to-Date', value: formatUsd(month?.total), sub: `${month?.calls || 0} calls`, color: 'text-blue-400' },
          { icon: Clock, label: 'Est. Full Month', value: formatUsd(month?.total ? month.total / Math.max(new Date().getDate(), 1) * 30 : 0), sub: 'projected', color: 'text-korinth-warning' },
          { icon: Zap, label: 'Claude Calls Today', value: String(today?.breakdown?.find(b => b.service?.includes('claude'))?.calls || 0), sub: formatUsd(today?.breakdown?.find(b => b.service?.includes('claude'))?.cost), color: 'text-purple-400' },
        ].map(({ icon: Icon, label, value, sub, color }) => (
          <div key={label} className="bg-korinth-bg rounded-lg border border-korinth-border/50 px-3 py-3 text-center">
            <Icon className={`w-4 h-4 ${color} mx-auto mb-1`} />
            <div className={`text-lg font-mono font-bold ${color}`}>{value}</div>
            <div className="text-[9px] text-korinth-muted">{label}</div>
            <div className="text-[8px] text-korinth-muted/60">{sub}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily cost chart */}
        <div>
          <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Daily Cost (7 days)</h4>
          <div className="h-40 bg-korinth-bg rounded-lg border border-korinth-border/30 p-2">
            {dailyData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyData}>
                  <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v.toFixed(2)}`} />
                  <Tooltip
                    contentStyle={{ background: '#111827', border: '1px solid #1f2937', borderRadius: 8, fontSize: 11 }}
                    formatter={(v) => [`$${Number(v).toFixed(4)}`, 'Cost']}
                  />
                  <Bar dataKey="cost" fill="#10b981" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-[10px] text-korinth-muted font-mono">No cost data yet</div>
            )}
          </div>
        </div>

        {/* Service breakdown */}
        <div>
          <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">By Service (7 days)</h4>
          <div className="h-40 bg-korinth-bg rounded-lg border border-korinth-border/30 p-2 flex items-center">
            {serviceData.length > 0 ? (
              <div className="flex items-center w-full">
                <ResponsiveContainer width="50%" height={130}>
                  <PieChart>
                    <Pie data={serviceData} dataKey="value" cx="50%" cy="50%" outerRadius={50} innerRadius={25}>
                      {serviceData.map((s, i) => <Cell key={i} fill={s.fill} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-1">
                  {serviceData.map((s, i) => (
                    <div key={i} className="flex items-center gap-2 text-[10px] font-mono">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: s.fill }} />
                      <span className="text-korinth-text flex-1">{s.name}</span>
                      <span className="text-korinth-muted">{s.calls} calls</span>
                      <span className="text-korinth-accent">{formatUsd(s.cost)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full text-[10px] text-korinth-muted font-mono">No data yet</div>
            )}
          </div>
        </div>
      </div>

      {/* Recent calls table */}
      <div className="mt-6">
        <h4 className="text-[10px] font-mono text-korinth-muted uppercase tracking-wider mb-2">Recent API Calls</h4>
        <div className="max-h-48 overflow-y-auto space-y-1">
          {recent.length > 0 ? recent.map((c, i) => (
            <div key={c.id || i} className="flex items-center gap-2 px-2 py-1.5 bg-korinth-bg rounded text-[10px] font-mono">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                c.status === 'success' ? 'bg-korinth-accent' : c.status === 'rate_limited' ? 'bg-korinth-warning' : 'bg-korinth-danger'
              }`} />
              <span className="text-korinth-text w-28 truncate">{c.service}</span>
              <span className="text-korinth-muted w-20 truncate">{c.endpoint || '—'}</span>
              {c.input_tokens > 0 && <span className="text-korinth-muted">{c.input_tokens}→{c.output_tokens}tok</span>}
              <span className="text-korinth-accent ml-auto">{c.cost_usd > 0 ? formatUsd(c.cost_usd) : 'free'}</span>
              <span className="text-korinth-muted/60 w-14 text-right">{c.created_at?.slice(11, 16)}</span>
            </div>
          )) : (
            <div className="text-center text-[10px] text-korinth-muted font-mono py-4">No API calls recorded yet</div>
          )}
        </div>
      </div>
    </ModuleContainer>
  );
}
